/* eslint-disable react-refresh/only-export-components */
import React, { useMemo, useState } from 'react';

import {
  ArrowDownward as ArrowDownIcon,
  ArrowUpward as ArrowUpIcon,
  Code as CodeIcon,
  Comment as CommentIcon,
  CompareArrows as CompareIcon,
  CloudUpload as DeployIcon,
  Edit as EditIcon,
  List as ExecutionLogIcon,
  Download as ExportIcon,
  UnfoldLess as FoldIcon,
  FormatIndentIncrease as FormatIcon,
  Storage as MetadataIcon,
  PictureInPicture as MinimapIcon,
  Redo as RedoIcon,
  PlayArrow as RunIcon,
  Save as SaveIcon,
  CalendarMonth as ScheduleIcon,
  Search as SearchIcon,
  TableChart as SqlIcon,
  Undo as UndoIcon,
  History as VersionIcon,
  AccountTree as VisualIcon,
} from '@mui/icons-material';
import {
  Dialog,
  DialogContent,
  List,
  ListItemButton,
  ListItemIcon,
  ListItemText,
  TextField,
  Typography,
} from '@mui/material';

import { formatShortcut } from '../hooks/useKeyboardShortcuts';

export interface Command {
  id: string;
  label: string;
  description: string;
  icon: React.ReactNode;
  shortcut?: {
    key: string;
    ctrl?: boolean;
    cmd?: boolean;
    shift?: boolean;
    alt?: boolean;
  };
  handler: () => void;
  category: 'File' | 'Edit' | 'Execute' | 'Version' | 'Deploy' | 'Tools' | 'View';
}

interface CommandPaletteProps {
  open: boolean;
  onClose: () => void;
  commands: Command[];
}

export const CommandPalette: React.FC<CommandPaletteProps> = ({ open, onClose, commands }) => {
  const [searchQuery, setSearchQuery] = useState('');
  const [selectedIndex, setSelectedIndex] = useState(0);

  // Filter commands based on search query
  const filteredCommands = useMemo(() => {
    if (!searchQuery.trim()) {
      return commands;
    }

    const query = searchQuery.toLowerCase();
    return commands.filter(
      (cmd) =>
        cmd.label.toLowerCase().includes(query) ||
        cmd.description.toLowerCase().includes(query) ||
        cmd.category.toLowerCase().includes(query)
    );
  }, [commands, searchQuery]);

  // Reset selection when filtered commands change
  React.useEffect(() => {
    setSelectedIndex(0);
  }, [filteredCommands]);

  // Reset search when opening
  React.useEffect(() => {
    if (open) {
      setSearchQuery('');
      setSelectedIndex(0);
    }
  }, [open]);

  // Keyboard navigation
  const handleKeyDown = (event: React.KeyboardEvent) => {
    if (event.key === 'ArrowDown') {
      event.preventDefault();
      setSelectedIndex((prev) => Math.min(prev + 1, filteredCommands.length - 1));
    } else if (event.key === 'ArrowUp') {
      event.preventDefault();
      setSelectedIndex((prev) => Math.max(prev - 1, 0));
    } else if (event.key === 'Enter' && filteredCommands[selectedIndex]) {
      event.preventDefault();
      executeCommand(filteredCommands[selectedIndex]);
    } else if (event.key === 'Escape') {
      event.preventDefault();
      onClose();
    }
  };

  const executeCommand = (command: Command) => {
    command.handler();
    onClose();
  };

  // Group commands by category
  const commandsByCategory = useMemo(() => {
    const grouped: Record<string, Command[]> = {};
    filteredCommands.forEach((cmd) => {
      if (!grouped[cmd.category]) {
        grouped[cmd.category] = [];
      }
      grouped[cmd.category].push(cmd);
    });
    return grouped;
  }, [filteredCommands]);

  return (
    <Dialog
      open={open}
      onClose={onClose}
      maxWidth="sm"
      fullWidth
      PaperProps={{
        sx: {
          position: 'fixed',
          top: '20%',
          m: 0,
        },
      }}
    >
      <DialogContent sx={{ p: 0 }}>
        <TextField
          autoFocus
          fullWidth
          placeholder="Search commands... (type to filter)"
          value={searchQuery}
          onChange={(e) => setSearchQuery(e.target.value)}
          onKeyDown={handleKeyDown}
          InputProps={{
            startAdornment: <SearchIcon sx={{ ml: 2, mr: 1, color: 'text.secondary' }} />,
          }}
          sx={{
            '& .MuiOutlinedInput-root': {
              '& fieldset': { border: 'none' },
            },
            borderBottom: 1,
            borderColor: 'divider',
          }}
        />
        <List sx={{ maxHeight: 400, overflow: 'auto', py: 1 }}>
          {Object.keys(commandsByCategory).length === 0 ? (
            <Typography variant="body2" color="text.secondary" sx={{ p: 2, textAlign: 'center' }}>
              No commands found
            </Typography>
          ) : (
            Object.entries(commandsByCategory).map(([category, categoryCommands]) => (
              <React.Fragment key={category}>
                <Typography
                  variant="caption"
                  color="text.secondary"
                  sx={{ px: 2, pt: 1, pb: 0.5, display: 'block', fontWeight: 600 }}
                >
                  {category}
                </Typography>
                {categoryCommands.map((cmd, _index) => {
                  const globalIndex = filteredCommands.indexOf(cmd);
                  return (
                    <ListItemButton
                      key={cmd.id}
                      onClick={() => executeCommand(cmd)}
                      selected={globalIndex === selectedIndex}
                      sx={{ py: 1 }}
                    >
                      <ListItemIcon sx={{ minWidth: 40 }}>{cmd.icon}</ListItemIcon>
                      <ListItemText
                        primary={cmd.label}
                        secondary={cmd.description}
                        primaryTypographyProps={{ variant: 'body2' }}
                        secondaryTypographyProps={{ variant: 'caption' }}
                      />
                      {cmd.shortcut && (
                        <Typography
                          variant="caption"
                          color="text.secondary"
                          sx={{
                            ml: 2,
                            px: 1,
                            py: 0.5,
                            bgcolor: 'action.hover',
                            borderRadius: 0.5,
                            fontFamily: 'monospace',
                          }}
                        >
                          {formatShortcut({ ...cmd.shortcut, description: cmd.description })}
                        </Typography>
                      )}
                    </ListItemButton>
                  );
                })}
              </React.Fragment>
            ))
          )}
        </List>
        <Typography
          variant="caption"
          color="text.secondary"
          sx={{
            display: 'block',
            px: 2,
            py: 1,
            borderTop: 1,
            borderColor: 'divider',
            bgcolor: 'action.hover',
          }}
        >
          <ArrowUpIcon sx={{ fontSize: 14, verticalAlign: 'middle', mr: 0.5 }} />
          <ArrowDownIcon sx={{ fontSize: 14, verticalAlign: 'middle', mr: 1 }} />
          Navigate • Enter to select • Esc to close
        </Typography>
      </DialogContent>
    </Dialog>
  );
};

/**
 * Hook to build default commands list
 */
export const useCommandPaletteCommands = (handlers: {
  // File
  undo: () => void;
  redo: () => void;
  save: () => void;
  toggleAutosave: () => void;
  export: () => void;
  // Edit
  comment: () => void;
  format: () => void;
  foldSchemas: () => void;
  // Execute
  runAsJob: () => void;
  testRecipe: () => void;
  viewExecutionLog: () => void;
  // Version
  viewVersions: () => void;
  createNewVersion: () => void;
  compareVersions: () => void;
  // Deploy
  deploy: () => void;
  // Tools
  schedule: () => void;
  metadata: () => void;
  templates: () => void;
  // View
  toggleMinimap: () => void;
  toggleSecondaryToolbar: () => void;
  switchEditorMode: () => void;
  editSqlQuery: () => void;
}): Command[] => {
  return [
    // File commands
    {
      id: 'undo',
      label: 'Undo',
      description: 'Undo last change',
      icon: <UndoIcon fontSize="small" />,
      shortcut: { key: 'z', cmd: true },
      handler: handlers.undo,
      category: 'File',
    },
    {
      id: 'redo',
      label: 'Redo',
      description: 'Redo last undone change',
      icon: <RedoIcon fontSize="small" />,
      shortcut: { key: 'z', cmd: true, shift: true },
      handler: handlers.redo,
      category: 'File',
    },
    {
      id: 'save',
      label: 'Save',
      description: 'Save recipe changes',
      icon: <SaveIcon fontSize="small" />,
      shortcut: { key: 's', cmd: true },
      handler: handlers.save,
      category: 'File',
    },
    {
      id: 'toggle-autosave',
      label: 'Toggle Autosave',
      description: 'Enable or disable autosave',
      icon: <SaveIcon fontSize="small" />,
      handler: handlers.toggleAutosave,
      category: 'File',
    },
    {
      id: 'export',
      label: 'Export Recipe',
      description: 'Download recipe as JSON',
      icon: <ExportIcon fontSize="small" />,
      handler: handlers.export,
      category: 'File',
    },

    // Edit commands
    {
      id: 'comment',
      label: 'Toggle Comment',
      description: 'Comment or uncomment selected lines',
      icon: <CommentIcon fontSize="small" />,
      shortcut: { key: '/', cmd: true },
      handler: handlers.comment,
      category: 'Edit',
    },
    {
      id: 'format',
      label: 'Format Document',
      description: 'Format the current document',
      icon: <FormatIcon fontSize="small" />,
      shortcut: { key: 'f', cmd: true, shift: true },
      handler: handlers.format,
      category: 'Edit',
    },
    {
      id: 'fold-schemas',
      label: 'Fold/Unfold Schemas',
      description: 'Toggle schema folding in YAML',
      icon: <FoldIcon fontSize="small" />,
      handler: handlers.foldSchemas,
      category: 'Edit',
    },

    // Execute commands
    {
      id: 'run-as-job',
      label: 'Run as Job',
      description: 'Execute recipe in production mode',
      icon: <RunIcon fontSize="small" />,
      handler: handlers.runAsJob,
      category: 'Execute',
    },
    {
      id: 'test-recipe',
      label: 'Test Recipe',
      description: 'Dry run with sample data',
      icon: <RunIcon fontSize="small" />,
      handler: handlers.testRecipe,
      category: 'Execute',
    },
    {
      id: 'execution-log',
      label: 'View Execution Log',
      description: 'Show execution history and logs',
      icon: <ExecutionLogIcon fontSize="small" />,
      handler: handlers.viewExecutionLog,
      category: 'Execute',
    },

    // Version commands
    {
      id: 'view-versions',
      label: 'View Versions',
      description: 'Browse all recipe versions',
      icon: <VersionIcon fontSize="small" />,
      handler: handlers.viewVersions,
      category: 'Version',
    },
    {
      id: 'new-version',
      label: 'Create New Version',
      description: 'Check in a new version',
      icon: <VersionIcon fontSize="small" />,
      handler: handlers.createNewVersion,
      category: 'Version',
    },
    {
      id: 'compare-versions',
      label: 'Compare Versions',
      description: 'Show diff between versions',
      icon: <CompareIcon fontSize="small" />,
      handler: handlers.compareVersions,
      category: 'Version',
    },

    // Deploy commands
    {
      id: 'deploy',
      label: 'Deploy to Environment',
      description: 'Deploy recipe to selected environment',
      icon: <DeployIcon fontSize="small" />,
      handler: handlers.deploy,
      category: 'Deploy',
    },

    // Tools commands
    {
      id: 'schedule',
      label: 'Manage Schedules',
      description: 'Configure recipe schedules/triggers',
      icon: <ScheduleIcon fontSize="small" />,
      handler: handlers.schedule,
      category: 'Tools',
    },
    {
      id: 'metadata',
      label: 'Explore Metadata',
      description: 'Browse environment metadata',
      icon: <MetadataIcon fontSize="small" />,
      handler: handlers.metadata,
      category: 'Tools',
    },
    {
      id: 'templates',
      label: 'Select Template',
      description: 'Choose DataMechanics template',
      icon: <CodeIcon fontSize="small" />,
      handler: handlers.templates,
      category: 'Tools',
    },

    // View commands
    {
      id: 'toggle-minimap',
      label: 'Toggle Minimap',
      description: 'Show/hide code minimap',
      icon: <MinimapIcon fontSize="small" />,
      handler: handlers.toggleMinimap,
      category: 'View',
    },
    {
      id: 'toggle-secondary-toolbar',
      label: 'Toggle Secondary Toolbar',
      description: 'Show/hide secondary toolbar',
      icon: <EditIcon fontSize="small" />,
      handler: handlers.toggleSecondaryToolbar,
      category: 'View',
    },
    {
      id: 'switch-editor-mode',
      label: 'Switch Editor Mode',
      description: 'Toggle between Visual and YAML editor',
      icon: <VisualIcon fontSize="small" />,
      handler: handlers.switchEditorMode,
      category: 'View',
    },
    {
      id: 'edit-sql',
      label: 'Edit SQL Query',
      description: 'Open SQL query editor',
      icon: <SqlIcon fontSize="small" />,
      handler: handlers.editSqlQuery,
      category: 'View',
    },
  ];
};
